/**
 * SaveSystem - Handles local storage persistence
 *
 * Features:
 * - Saves player progress, scores, stats, and settings
 * - Version-based migration for schema changes
 * - Graceful handling of corrupted data
 * - Auto-save on key events
 *
 * Per PRD section 8.3 and NFR-014 (progress never lost during normal use)
 */

// Storage keys for localStorage
const NEW_SAVE_KEY = 'claw_shooter_save';
const OLD_SAVE_KEY = 'crowd_shooter_save';

// Current save data version - increment when schema changes
const CURRENT_VERSION = 1;

/**
 * Player statistics tracked across all gameplay
 */
export interface PlayerStats {
  totalKills: number;
  totalSoldiersCollected: number;
  totalLevelsCompleted: number;
  totalPlayTimeSeconds: number;
}

/**
 * User settings
 */
export interface GameSettings {
  soundEnabled: boolean;
  musicEnabled: boolean;
}

/**
 * Complete save data structure
 * Matches PRD section 8.3 specification
 */
export interface SaveData {
  version: number;
  highestLevel: number; // 1-10, highest unlocked level
  currentTier: number; // 1, 2, 3... current difficulty tier
  levelScores: Record<string, number>; // "tier-level" -> highScore
  stats: PlayerStats;
  settings: GameSettings;
}

/**
 * Returns fresh default save data for new players
 */
function getDefaultSaveData(): SaveData {
  return {
    version: CURRENT_VERSION,
    highestLevel: 1,
    currentTier: 1,
    levelScores: {},
    stats: {
      totalKills: 0,
      totalSoldiersCollected: 0,
      totalLevelsCompleted: 0,
      totalPlayTimeSeconds: 0,
    },
    settings: {
      soundEnabled: true,
      musicEnabled: true,
    },
  };
}

/**
 * SaveSystem manages all persistent game data
 *
 * Usage:
 *   const saveSystem = new SaveSystem();
 *   saveSystem.load(); // Call once at game start
 *   saveSystem.unlockLevel(2); // Automatically saves
 */
export class SaveSystem {
  private data: SaveData;

  constructor() {
    this.data = getDefaultSaveData();
  }

  /**
   * Loads save data from localStorage
   * Call this once when the game starts
   *
   * If data is corrupted or missing, returns default save data
   * If data is from older version, migrates it to current version
   */
  load(): SaveData {
    try {
      const rawData = localStorage.getItem(NEW_SAVE_KEY) ?? localStorage.getItem(OLD_SAVE_KEY);
      const shouldMigrateStorageKey = !localStorage.getItem(NEW_SAVE_KEY) && !!localStorage.getItem(OLD_SAVE_KEY);

      if (!rawData) {
        // New player - use defaults
        this.data = getDefaultSaveData();
        return this.data;
      }

      const parsed = JSON.parse(rawData);

      // Validate that it's an object with expected shape
      if (!this.isValidSaveData(parsed)) {
        console.warn('SaveSystem: Invalid save data detected, using defaults');
        this.data = getDefaultSaveData();
        this.save(); // Overwrite corrupted data
        return this.data;
      }

      // Migrate if needed
      if (parsed.version < CURRENT_VERSION) {
        this.data = this.migrate(parsed);
        this.save(); // Save migrated data
      } else {
        this.data = parsed;
        if (shouldMigrateStorageKey) {
          // Promote legacy save payload to the new key without resetting progress.
          this.save();
        }
      }

      return this.data;
    } catch (error) {
      // JSON parse failed or other error
      console.error('SaveSystem: Error loading save data:', error);
      this.data = getDefaultSaveData();
      return this.data;
    }
  }

  /**
   * Saves current data to localStorage
   * Called automatically by update methods
   */
  save(): boolean {
    try {
      const serialized = JSON.stringify(this.data);
      localStorage.setItem(NEW_SAVE_KEY, serialized);
      return true;
    } catch (error) {
      // localStorage might be full or disabled
      console.error('SaveSystem: Error saving data:', error);
      return false;
    }
  }

  /**
   * Resets all progress to defaults
   * Use with caution - typically behind a confirmation dialog
   */
  reset(): void {
    this.data = getDefaultSaveData();
    this.save();
  }

  // ========== Level Progress ==========

  /**
   * Unlocks a level (and all previous levels)
   * Typically called when completing a level
   */
  unlockLevel(level: number, tier?: number): void {
    const targetTier = tier ?? this.data.currentTier;

    // Only update if this is the current tier
    if (targetTier === this.data.currentTier) {
      if (level > this.data.highestLevel) {
        this.data.highestLevel = Math.min(level, 10);
      }
    }

    this.save();
  }

  /**
   * Advances to the next tier (called after beating level 10)
   */
  advanceTier(): void {
    this.data.currentTier += 1;
    this.data.highestLevel = 1; // Reset to level 1 for new tier
    this.save();
  }

  /**
   * Returns the highest unlocked level (1-10)
   */
  getHighestLevel(): number {
    return this.data.highestLevel;
  }

  /**
   * Returns the current tier
   */
  getCurrentTier(): number {
    return this.data.currentTier;
  }

  /**
   * Checks if a specific level is unlocked
   */
  isLevelUnlocked(level: number, tier?: number): boolean {
    const targetTier = tier ?? this.data.currentTier;

    if (targetTier < this.data.currentTier) {
      // Previous tiers are fully unlocked
      return true;
    }

    if (targetTier > this.data.currentTier) {
      // Future tiers are locked
      return false;
    }

    // Current tier - check level
    return level <= this.data.highestLevel;
  }

  // ========== Scores ==========

  /**
   * Records a level score (only saves if it's a new high score)
   * Returns true if this was a new high score
   */
  setLevelScore(level: number, score: number, tier?: number): boolean {
    const targetTier = tier ?? this.data.currentTier;
    const key = `${targetTier}-${level}`;
    const currentHigh = this.data.levelScores[key] || 0;

    if (score > currentHigh) {
      this.data.levelScores[key] = score;
      this.save();
      return true;
    }

    return false;
  }

  /**
   * Gets the high score for a specific level
   */
  getLevelScore(level: number, tier?: number): number {
    const targetTier = tier ?? this.data.currentTier;
    const key = `${targetTier}-${level}`;
    return this.data.levelScores[key] || 0;
  }

  /**
   * Gets all level scores
   */
  getAllScores(): Record<string, number> {
    return { ...this.data.levelScores };
  }

  // ========== Stats ==========

  /**
   * Updates player statistics
   * Pass only the fields you want to increment
   */
  updateStats(updates: Partial<PlayerStats>): void {
    if (updates.totalKills !== undefined) {
      this.data.stats.totalKills += updates.totalKills;
    }
    if (updates.totalSoldiersCollected !== undefined) {
      this.data.stats.totalSoldiersCollected += updates.totalSoldiersCollected;
    }
    if (updates.totalLevelsCompleted !== undefined) {
      this.data.stats.totalLevelsCompleted += updates.totalLevelsCompleted;
    }
    if (updates.totalPlayTimeSeconds !== undefined) {
      this.data.stats.totalPlayTimeSeconds += updates.totalPlayTimeSeconds;
    }

    this.save();
  }

  /**
   * Gets current player stats
   */
  getStats(): PlayerStats {
    return { ...this.data.stats };
  }

  // ========== Settings ==========

  /**
   * Updates game settings
   */
  updateSettings(updates: Partial<GameSettings>): void {
    if (updates.soundEnabled !== undefined) {
      this.data.settings.soundEnabled = updates.soundEnabled;
    }
    if (updates.musicEnabled !== undefined) {
      this.data.settings.musicEnabled = updates.musicEnabled;
    }

    this.save();
  }

  /**
   * Gets current settings
   */
  getSettings(): GameSettings {
    return { ...this.data.settings };
  }

  /**
   * Quick check if sound is enabled
   */
  isSoundEnabled(): boolean {
    return this.data.settings.soundEnabled;
  }

  /**
   * Quick check if music is enabled
   */
  isMusicEnabled(): boolean {
    return this.data.settings.musicEnabled;
  }

  // ========== Internal Helpers ==========

  /**
   * Validates that loaded data has the expected structure
   */
  private isValidSaveData(data: unknown): data is SaveData {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const obj = data as Record<string, unknown>;

    // Check required top-level fields
    if (typeof obj.version !== 'number') return false;
    if (typeof obj.highestLevel !== 'number') return false;
    if (typeof obj.currentTier !== 'number') return false;
    if (typeof obj.levelScores !== 'object') return false;

    // Check stats object
    if (typeof obj.stats !== 'object' || obj.stats === null) return false;
    const stats = obj.stats as Record<string, unknown>;
    if (typeof stats.totalKills !== 'number') return false;
    if (typeof stats.totalSoldiersCollected !== 'number') return false;
    if (typeof stats.totalLevelsCompleted !== 'number') return false;
    if (typeof stats.totalPlayTimeSeconds !== 'number') return false;

    // Check settings object
    if (typeof obj.settings !== 'object' || obj.settings === null) return false;
    const settings = obj.settings as Record<string, unknown>;
    if (typeof settings.soundEnabled !== 'boolean') return false;
    if (typeof settings.musicEnabled !== 'boolean') return false;

    return true;
  }

  /**
   * Migrates old save data to current version
   * Add migration logic here when CURRENT_VERSION increases
   */
  private migrate(oldData: SaveData): SaveData {
    const migrated = { ...oldData };

    // Example migration pattern for future versions:
    // if (migrated.version < 2) {
    //   // Add new field introduced in version 2
    //   migrated.newField = defaultValue;
    //   migrated.version = 2;
    // }

    // Ensure version is current
    migrated.version = CURRENT_VERSION;

    return migrated;
  }

  /**
   * Gets the raw save data (mainly for debugging)
   */
  getRawData(): SaveData {
    return { ...this.data };
  }
}

// Export a singleton instance for easy access across the game
export const saveSystem = new SaveSystem();
